import type es from '../es/onboarding';

/** `onboarding` in English. Same keys as `es/onboarding.ts`, enforced by the type. */
/** "1 detail" / "2 details". */
function details(n: number): string {
  return n === 1 ? '1 detail' : `${n} details`;
}

/** "1 more detail" / "2 more details". */
function moreDetails(n: number): string {
  return n === 1 ? '1 more detail' : `${n} more details`;
}

/** "1 point" / "6 points". */
function points(n: number): string {
  return n === 1 ? '1 point' : `${n} points`;
}

const onboarding: typeof es = {
  landing: {
    title: 'Let AI apply for you',
    lede: 'Connect your Gmail, upload your resume, and set your preferences. We will send tailored applications from your email, every day.',
    cta: 'Get started',
    /** La línea de la app debajo del CTA. */
    app: 'Get the app:',
    playSoon: 'Google Play coming soon',
    partners: 'Recommend TBit Jobs? See the partner program',
  },

  errors: {
    missing_gmail_scope: "We didn't get the Gmail permissions we need. Try again and accept all the permissions.",
    auth_failed: 'Something went wrong while signing you in. Try again.',
    no_code: "We didn't receive the authorization code. Try again.",
    not_authenticated: 'You need to sign in first.',
    gmail_signup_disabled: 'For now, new accounts use the email address TBit manages.',
    unknown: (code: string) => `Error: ${code}`,
  },

  client: {
    domain_channel_disabled: "We're turning on sending. Try again in a moment.",
    unauthorized: 'Your session expired. Sign in again.',
    network: "You're offline. Check your connection and try again.",
    unknown: "We couldn't activate your account. Try again.",
  },

  /** `ConsentStep`: terms + public profile, on signup (and once for existing accounts). */
  consent: {
    step: 'Before you enter',
    title: 'Who can see your profile?',
    lede: 'With a public profile, employers can find you on Google and on TBit Jobs without you applying one by one.',
    groupLabel: 'Who can see your profile',
    publicLabel: 'Public',
    recommended: 'Recommended',
    publicHint: 'Your profile gets its own page on TBit Jobs and can show up on Google.',
    privateLabel: 'Private',
    privateHint: 'Only agencies in the talent pool see you, without your contact details.',
    seenTitle: 'What is shown',
    seenBody: 'Your first and last name, your trade, your years of experience, your skills and your country.',
    neverTitle: 'What is never shown',
    neverBody: 'Your email, your phone, your resume PDF, your score and your immigration status.',
    ack: {
      before: 'I understand that my profile will be ',
      strong: 'public',
      after: ' and that Google will be able to show it. I can make it private whenever I want in Profile → Who finds you.',
      required: 'Check that you understand your profile will be public, or choose Private.',
    },
    terms: {
      before: 'I accept the ',
      label: 'TBit Jobs Terms and Conditions',
      href: 'https://tbit.app/legal/tbit-jobs-terms-and-conditions',
      after: '.',
      required: 'Check the box to accept the terms and continue.',
      outdated: 'We updated the terms. Read them and check the box again.',
    },
    cta: 'Go to my dashboard',
    saving: 'Saving…',
    noCard: "We don't ask for a card.",
    close: 'Close',
    closeNote: 'If you close this, your profile stays private until you decide.',
    saveFailed: "We couldn't save your choice. Try again.",
    connectionError: 'Connection error. Try again.',
  },

  done: {
    title: "You're all set!",
    body: 'Your account is ready.',
    cta: 'Go to dashboard',
  },

  sender: {
    title: 'Start applying today',
    lede: 'We provide the email address your applications go out from, and we forward every reply to the email you already use.',
    pointSend: 'We send a tailored application for every job',
    pointReplies: 'Every reply from a company lands in your inbox',
    pointNoSpam: 'We never send spam or anything you did not approve',
    activationFailed: "We couldn't activate your account. Try again.",
    ctaManaged: 'Continue with TBit-managed sending',
    ctaStart: 'Get started',
    ctaHint: 'No account to connect. It takes less than a minute.',
    or: 'or',
    ctaGmail: "I'd rather apply from my Gmail",
    moreInfo: 'More information',
    managedTitle: 'TBit-managed sending:',
    managedBody: 'your applications go out from an address in your name that we manage, and every reply is forwarded to your email. We never ask for a password.',
    gmailTitle: 'From your Gmail:',
    gmailBody: 'we ask for permission to read and send email so we can (1) send applications from your Gmail account and (2) detect when a company replies to your application. Your credentials are encrypted and never shared with third parties. You can revoke access at any time from your Google account.',
  },

  cvGate: {
    step: 'Step 2 of 2',
    title: 'Your resume',
    lede: "It's what we send to every company. Choose how you want to set it up.",
    uploadTitle: 'Upload your resume',
    uploadLede: 'If you already have it as a PDF or Word file, we read it and pull out your experience.',
    uploadBullets: ['Takes less than a minute', 'PDF or DOCX, up to 5 MB'],
    uploadCta: 'Upload file',
    buildTitle: 'Build your resume with us',
    buildLede: 'You answer a few questions and we write it for you.',
    buildBullets: [
      "It comes out in English, the way employers there expect it",
      'In the format DOL employers read',
    ],
    buildCta: 'Start the questions',
  },

  upload: {
    step: 'Step 2 of 2',
    title: 'Upload your resume',
    lede: "We'll pull out your skills and experience to tailor your applications.",
    ledeStandalone: "Upload a PDF or DOCX file. We'll pull out your skills and experience to tailor your applications.",
    invalidType: "That file isn't a PDF or Word (DOCX) file. Choose your resume in one of those two formats.",
    tooLarge: 'The file is larger than 5 MB. Save it again as a PDF or compress it, then choose it again.',
    unreadable:
      "We couldn't open this file on your phone. It often happens with files from Drive or WhatsApp: download it first (so it's in Downloads) and choose it again.",
    network:
      "We couldn't send your resume because the connection dropped. Check your internet (Wi-Fi usually works better) and try again.",
    questionnaireHint: (button: string) =>
      `If it keeps failing, tap "${button}" and we'll build it with you through a few questions.`,
    dropFile: 'Drag your file here',
    dropHint: 'PDF or DOCX, max 5 MB',
    dropCv: 'Drag your resume here',
    dropCvHint: 'or click to browse (PDF, DOCX, max 5 MB)',
    httpError: (status: number) =>
      `We couldn't process your resume (error ${status}). Try again in a few minutes.`,
    failed: "We couldn't upload the file. Try again.",
    submit: 'Upload resume',
    notAtHand: "I don't have it handy",
    uploaded: 'Resume uploaded.',
    continue: 'Continue',
    processed: 'Resume processed',
    skillsTitle: 'Skills we found',
    noSkills: "We didn't find any skills.",
    extracted: (chars: string) => `We extracted ${chars} characters from the document.`,
  },

  whatsapp: {
    label: 'Your WhatsApp',
    optional: ' (optional)',
    consent: 'We message you on WhatsApp about your applications and if an employer wants to assess your English.',
    country: 'Country',
    placeholder: '300 123 4567',
    invalid: 'Check the number: pick your country and type it without the country code.',
    saveFailedAfterCv: 'We saved your resume, but not your WhatsApp. You can add it in Profile.',
    empty: 'You have not given us your WhatsApp yet.',
    edit: 'Change',
    add: 'Add my WhatsApp',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Done, we saved your WhatsApp.',
    failed: 'We could not save your WhatsApp. Try again.',
  },

  profile: {
    title: 'Your profile',
    navLabel: 'Profile sections',
    viewPublicProfile: 'View my public profile',
    enablePublicProfile: 'Turn on my public profile',
    /** The talent pool tab name with the free tier switch off (the old one). */
    talentPoolTabLegacy: 'Talent pool',
    tabs: {
      cv: 'Resume',
      preferences: 'Preferences',
      talentPool: 'Who finds you',
      plan: 'Plan',
      partners: 'Partner program',
      english: 'English',
    },
    ledes: {
      cv: 'Everything starts here: we use this document to write every application and build the summary agencies see.',
      preferences:
        "Tell us what you're looking for. Agencies that post jobs with us use this to see whether a job fits you before reaching out.",
      talentPool: 'Decide whether agencies can find you without you having to apply to each one.',
      plan: 'Your plan is what keeps us applying for you every business day. Here you can see how long it runs.',
      partners: 'Recommend TBit Jobs with your code and earn for every person who subscribes.',
      english: 'Your English assessments: the ones companies ask for and your results.',
    },
  },

  cvPage: {
    scoreUnavailable: "We couldn't score it right now. Try again in a moment.",
    scoring: 'Reviewing your resume',
    retry: 'Try again',
    scope: 'How strong it is',
    addMissing: 'Add these details',
    reviewAnswers: 'Go over my answers',
    orUploadBelow: 'Or upload a new version of the file below.',
    uploadNew: 'Upload a new version',
  },

  cvScore: {
    defaultScope: 'Your resume',
    gauge: (score: number) => `Score ${score} out of 100`,
    gapsTitle: "What it's missing",
    addGap: 'Add this',
    winsTitle: 'What already works in your favor',
    readFailed: "We couldn't load your score. Try again.",
    labels: {
      strong: 'Ready to send',
      fair: "You're on the right track",
      weak: 'It needs more to go on',
    },
    headline: {
      strongReady: 'Your resume is ready to send.',
      strongGaps: (gaps: number) => `Your resume is ready to send. Add ${moreDetails(gaps)} and it's spotless.`,
      fairNoGaps: "You're doing well. Add concrete details to take it to the next level.",
      fairGaps: (gaps: number) => `You're ${details(gaps)} away from a strong resume.`,
      weakNoGaps: "Tell us more about what you've done and your resume will reach a whole new level.",
      weakGaps: (gaps: number) => `You're ${details(gaps)} away from an employer taking you seriously.`,
    },
    subscores: {
      experience: { label: 'Experience', hint: "How long you've worked and doing what" },
      skills: { label: 'Skills', hint: 'Machines, tools, certifications' },
      specificity: { label: 'Concrete details', hint: 'Numbers, amounts, how long' },
      completeness: { label: 'How complete', hint: 'No missing sections' },
    },
  },

  improveCV: {
    title: 'Improve my resume',
    intro: 'Answer what you can and save everything together at the end. Each answer is saved in its place and is never deleted.',
    /** One button for every card: the score is recalculated once. */
    saveAll: (n: number) => (n === 0 ? 'Save answers' : n === 1 ? 'Save 1 answer' : `Save ${n} answers`),
    saveAllHint: 'Your score is recalculated once, when you save.',
    howItWorks: 'How your score is calculated (in Spanish)',
    dismissed: "You marked that you don't have this. It's saved with your other answers.",
    undoDismiss: 'Undo',
    save: 'Save',
    dismiss: "I don't have this",
    saving: "We're updating your resume",
    up: (n: number) => `Up ${points(n)}`,
    same: 'Stayed the same: we already had that detail',
    down: (n: number, reason: string) => `Down ${points(n)}: ${reason}`,
    noGaps: "Nothing important is missing. If you want, add another job or a course.",
    myData: 'My details',
    addJob: 'Add another job',
    addEducation: 'Add education',
    addLicense: 'Add a license',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Are you sure? This really gets deleted.',
    recovered: 'Recovered from an earlier version',
    methodChange:
      "We recalculated your score with the new method: now every detail you give adds up, and it doesn't drop when you add more.",
    score: (n: number) => `Your score: ${n} out of 100`,
    loading: 'Loading your resume',
    loadFailed: "We couldn't load your resume. Try again.",
    retry: 'Try again',
    saveFailed: "We couldn't save. Try again.",
    busy: 'Your resume was being saved somewhere else. Try again.',
    gapGone: "That question isn't on your resume anymore.",
    answerIsQuestion:
      'That looks like a question. Above it, it says which job or detail this is about: answer with the detail, or tap "I don\'t have this".',
    alreadySaid: (text: string) => `You already told us: "${text}"`,
    checkFields: 'Check what you wrote.',
    noChange: "You didn't change anything.",
    singletonExists: 'You already have that section: edit it in My details instead of adding another one.',
    leftoverTitle: "You wrote this afterwards and it isn't saved yet:",
    leftoverSave: 'Save as extra detail',
    leftoverDiscard: 'Discard',
    noProfile: "You don't have a resume yet. Upload one or answer the questionnaire.",
    toQuestionnaire: 'Answer the questionnaire',
    back: 'Back to my resume',
    invitation: 'Tell us in your own words. You can talk or type.',
    preview: 'How it shows on your resume',
    notes: 'What you told us',
    deleteNote: 'Delete this note',
    cancel: 'Cancel',
    current: 'Current',
    endYearNeeded: "If you don't work here anymore, tell us the year you finished.",
    kinds: {
      job: 'Job',
      education: 'Education',
      license: 'License',
      certification: 'Course or certificate',
      skills: 'Skills',
      languages: 'Languages',
      physical: 'Physical ability',
      other: 'Other detail',
    },
    fields: {
      company: 'Company or farm',
      role: 'Position',
      start_year: 'Year you started',
      end_year: 'Year you finished',
      stillThere: 'I still work here',
      level: 'What you studied',
      institution: 'School or institution',
      city: 'City',
      year: 'Year',
      type: 'License type',
      country: 'Country',
      since_year: "Year you've had it since",
      name: 'Course or certificate name',
      issuer: 'Who gave it',
      label: 'What it is',
      items: 'Separate them with commas',
      text: 'In your own words',
    },
  },

  preferences: {
    loadFailed: "We couldn't load your preferences. Reload the page.",
    saveFailed: "We couldn't save your preferences.",
    saved: 'Done, we saved your preferences.',
    connectionError: 'Connection error. Try again.',
    loading: 'Loading your preferences',
    rolesLabel: "Jobs you're looking for",
    rolesPlaceholder: 'E.g. Harvest worker, Kitchen helper',
    rolesHint: 'Separate them with commas.',
    locationsLabel: 'Where you want to work',
    locationsPlaceholder: 'E.g. Florida, Texas, Remote',
    jobTypesLabel: 'Job type',
    industriesLabel: "Industries you're interested in",
    industriesPlaceholder: 'E.g. Agriculture, Hospitality, Construction',
    salaryLabel: 'Expected pay',
    salaryMinPlaceholder: 'From',
    salaryMaxPlaceholder: 'To (optional)',
    currencyPlaceholder: 'USD',
    salaryHint: 'If you leave "to" empty, we take it that you have no ceiling. Leave everything blank if you prefer not to say.',
    excludedLabel: "Companies you don't want to see",
    excludedPlaceholder: 'E.g. your current employer',
    excludedHint: "We won't suggest you to these companies, and you won't show up in their searches.",
    submit: 'Save preferences',
    countriesPointer: 'The countries we apply to for you are chosen on your Dashboard.',
    countriesPointerLink: 'Go to Dashboard',
    salaryRangeInverted: "The maximum pay can't be lower than the minimum.",
    salaryPeriods: {
      hour: 'per hour',
      day: 'per day',
      week: 'per week',
      month: 'per month',
      year: 'per year',
    },
    jobTypes: {
      full_time: 'Full-time',
      part_time: 'Part-time',
      temporary: 'Temporary',
      seasonal: 'Seasonal',
      contract: 'Contract',
    },
  },

  talentPool: {
    loadFailed: "We couldn't read your choice. Reload the page.",
    saveFailed: "We couldn't save your choice.",
    joined: "You're now in the talent pool. You can leave whenever you want.",
    left: "You left the talent pool. You won't show up in new searches.",
    connectionError: 'Connection error. Try again.',
    loading: 'Loading your choice',
    title: 'Who finds you',
    /** With the free tier switch off, the old title. */
    titleLegacy: 'Let agencies find you',
    on: 'On',
    off: 'Off',
    lede: 'Agencies that post jobs with us can search for you by your experience, without you having to apply to each one.',
    toggleLabel: 'Show up in the talent pool',
    profileTitle: 'Public profile',
    profileLede: 'Your profile gets its own page on TBit Jobs and can show up on Google.',
    profileView: 'View my public profile',
    profileCopy: 'Copy link',
    profileCopied: 'Link copied',
    profileToggleLabel: 'Public profile',
    profileMadePublic: 'Your profile is public.',
    profileMadePrivate: 'Your profile is private. Your page stops showing within a few minutes.',
    profileSave: 'Make my profile public',
    profileCancel: 'Cancel',
    seenTitle: 'What they see',
    seenBody: 'Your first and last name, your experience, and your skills: a summary of your resume, without your personal details.',
    neverSeenTitle: 'What they never see',
    neverSeenBody: 'Your email and phone number. If an agency wants to talk to you, you get an invitation and you decide whether to accept. Only then do they get your details.',
    footerOn: 'You can leave whenever you want: you stop showing up in new searches right away.',
    footerOff: "You're out of the pool. Your resume is only used for the applications we send for you, and no agency can find you on its own.",
  },

  immigration: {
    step: 'One more question',
    title: 'Your immigration status',
    lede: 'This helps us explain your case to each U.S. employer. Pick the option that fits you.',
    groupLabel: 'Your immigration status',
    options: {
      first_time: { label: 'First time', hint: 'You have never had an H-2A or H-2B visa.' },
      returner: {
        label: 'Returner',
        hint: 'You worked on an H-2A or H-2B visa in past seasons and you are outside the United States now.',
      },
      extension: {
        label: 'Extension',
        hint: 'You are in the United States on an H-2 visa and your current employer will extend your stay.',
      },
      transfer: {
        label: 'Transfer',
        hint: 'You are in the United States on an H-2 visa and you want to move to a new employer.',
      },
    },
    employerLabel: 'Which employer, and which season?',
    employerOptional: ' (optional)',
    employerPlaceholder: 'E.g. Sunny Farms, 2025 season',
    employerHint: 'We add it to your emails so the employer sees you already know visa work.',
    note: 'This does not change which jobs we apply to: only how we introduce you.',
    submit: 'Save and continue',
    save: 'Save',
    saved: 'Done, we saved your immigration status.',
    pickOne: 'Pick an option to continue.',
    saveFailed: "We couldn't save your answer. Please try again.",
    connectionError: 'Connection error. Please try again.',
    cardLede: 'How we introduce you to U.S. employers. You can change it any time.',
    /** La pregunta de papeles de la UE: a quien eligió Europa (EURES). */
    eu: {
      lede: 'This tells us which jobs in Europe to apply to for you. Pick the option that fits you.',
      question: 'Can you work legally in the European Union?',
      groupLabel: 'Can you work legally in the European Union?',
      options: {
        citizen: { label: 'Yes, I have a passport from an EU country' },
        resident: { label: 'Yes, I have a valid residence or work permit in an EU country' },
        none: { label: 'No' },
      },
      noneHint:
        'We apply to jobs that say they accept candidates from outside the EU or help with the visa, and to trades with a shortage work permit in Spain: electricians, aluminium, metal or PVC carpentry, crane operators and merchant navy.',
      note: 'This changes which jobs in Europe we apply to and what we say in your emails.',
      pickOne: 'Tell us whether you can work in the European Union to continue.',
      cardTitle: 'Working in the European Union',
      cardLede: 'With EU papers we apply to every job in Europe; without them, to the ones that accept candidates from outside and to trades with a shortage work permit in Spain. You can change it any time.',
      saved: 'Done, we saved your answer.',
    },
  },
};

export default onboarding;
